import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORY_LABELS, PROPERTY_TYPES } from "@/lib/api";
import { useDemo } from "@/context/DemoContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { TrendUp, TrendDown, Buildings, Tray, ArrowRight } from "@phosphor-icons/react";
import HomeStrip from "@/components/obligations/HomeStrip";

const Stat = ({ label, value, accent, testid, sub }) => (
  <div data-testid={testid} className="bg-card border border-border p-5 rounded-sm">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className={`mt-3 font-display text-3xl font-black tabular-nums tracking-tight ${accent || ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const { fmtMoney } = useDemo();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading overview…</div>;

  const t = data.totals;
  const net = t.net;

  return (
    <div className="space-y-8 animate-fade-up">
      <HomeStrip />

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Portfolio</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Overview</h1>
        </div>
        {data.pending_bills > 0 && (
          <button
            data-testid="pending-bills-banner"
            onClick={() => navigate("/inbox")}
            className="flex items-center gap-2 bg-foreground text-background px-4 py-2.5 rounded-sm text-sm font-semibold transition-transform hover:-translate-y-[1px]"
          >
            <Tray size={16} weight="fill" />
            {data.pending_bills} bill{data.pending_bills > 1 ? "s" : ""} to review
            <ArrowRight size={16} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat testid="stat-income" label="Total Income" value={fmtMoney(t.income)} accent="text-success" />
        <Stat testid="stat-expense" label="Total Expense" value={fmtMoney(t.expense)} accent="text-destructive" />
        <Stat
          testid="stat-net"
          label="Net Cash Flow"
          value={fmtMoney(net)}
          accent={net >= 0 ? "text-success" : "text-destructive"}
        />
        <Stat
          testid="stat-portfolio"
          label="Portfolio"
          value={data.property_count}
          sub={`${data.unit_count} rentable units`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Monthly chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">
            Income vs Expense (Monthly)
          </p>
          {data.monthly.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
              No transactions yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  formatter={(v) => fmtMoney(v)}
                  contentStyle={{ borderRadius: 2, border: "1px solid hsl(var(--border))", fontSize: 12 }}
                />
                <Bar dataKey="income" fill="hsl(var(--success))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="expense" fill="hsl(var(--destructive))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Category breakdown */}
        <div className="bg-card border border-border rounded-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">Expense Mix</p>
          {data.category_breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No expenses recorded.</p>
          ) : (
            <div className="space-y-3">
              {data.category_breakdown.map((c) => {
                const max = data.category_breakdown[0].amount || 1;
                return (
                  <div key={c.category}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{CATEGORY_LABELS[c.category] || c.category}</span>
                      <span className="font-mono tabular-nums">{fmtMoney(c.amount)}</span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-foreground" style={{ width: `${(c.amount / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Property performance table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <Buildings size={18} weight="bold" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Per-Property Performance</p>
        </div>
        {data.properties.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No properties yet. <button onClick={() => navigate("/properties")} className="underline font-semibold">Add your first property</button>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-bold p-4">Property</th>
                  <th className="text-left font-bold p-4 hidden sm:table-cell">Type</th>
                  <th className="text-right font-bold p-4">Income</th>
                  <th className="text-right font-bold p-4">Expense</th>
                  <th className="text-right font-bold p-4">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.properties.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`dashboard-property-row-${p.id}`}
                    onClick={() => navigate(`/properties/${p.id}`)}
                    className="border-b border-border last:border-0 hover:bg-secondary cursor-pointer transition-colors"
                  >
                    <td className="p-4 font-semibold">
                      {p.name}
                      <span className="block text-xs text-muted-foreground font-normal">
                        {[p.city, p.state].filter(Boolean).join(", ")}
                      </span>
                    </td>
                    <td className="p-4 hidden sm:table-cell text-muted-foreground">{PROPERTY_TYPES[p.property_type]}</td>
                    <td className="p-4 text-right font-mono tabular-nums text-success">{fmtMoney(p.income)}</td>
                    <td className="p-4 text-right font-mono tabular-nums text-destructive">{fmtMoney(p.expense)}</td>
                    <td className={`p-4 text-right font-mono tabular-nums font-bold ${p.net >= 0 ? "text-success" : "text-destructive"}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {p.net >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                        {fmtMoney(p.net)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
